import { useRef, useState, ChangeEvent, useEffect } from 'react';
import styles from '../VerifyEmail/VerifyEmail.module.css';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../auth/Auth';
import clsx from 'clsx';
import { motion } from 'framer-motion';
import { ChevronLeftIcon, RotateCw } from 'lucide-react';
import { AuthBrandedLoader } from '../Login/AuthBrandedLoader';
import loginStyles from '../Login/Login.module.css';
import { AuthPageShell } from '../Login/AuthPageShell';
import {
  createLoginToken,
  getTenantDetails,
  sendVerificationCodePhone,
  verifyVerificationCodePhone,
} from '../../auth/_requests';
import { CANANA_COUNTRY_CODE } from '../../constants/canadaCountryCode';
import { trackOnboardingStepCompleted } from '../../helpers/utils/analytics';

export function VerifyPhone() {
  const { auth, currentUser, logout, saveAuth } = useAuth();
  const inputRefs = [
    useRef<HTMLInputElement>(null),
    useRef<HTMLInputElement>(null),
    useRef<HTMLInputElement>(null),
    useRef<HTMLInputElement>(null),
  ];
  const [inputValues, setInputValues] = useState(['', '', '', '']);
  const [errorMessage, setErrorMessage] = useState('');
  const [validMessage, setValidMessage] = useState('');
  const codeSentRef = useRef(false);
  const [loading] = useState(false);
  const [isPhoneVerified, setIsPhoneVerified] = useState(false);
  const [loadingVerify, setLoadingVerify] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(900); // 15 minutes
  const [resendEnabled, setResendEnabled] = useState(false);

  const navigate = useNavigate();

  // Format as mm:ss
  const formatTime = (seconds: number) => {
    const m = Math.floor(seconds / 60)
      .toString()
      .padStart(2, '0');
    const s = (seconds % 60).toString().padStart(2, '0');
    return `${m}:${s}`;
  };

  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (secondsLeft > 0) {
      timer = setInterval(() => {
        setSecondsLeft((prev) => prev - 1);
      }, 1000);
    } else {
      setResendEnabled(true);
    }
    return () => clearInterval(timer);
  }, [secondsLeft]);

  const handleInputChange = (index: number) => (e: ChangeEvent<HTMLInputElement>) => {
    let value = e.target.value.slice(0, 1);
    if (!/^\d$/.test(value)) value = '';
    const newValues = [...inputValues];
    newValues[index] = value;
    setInputValues(newValues);
    if (value.length === 1 && index < 3) inputRefs[index + 1].current?.focus();
  };

  const maskPhone = (phone: string): string => {
    if (!phone) return '';
    const lastTwo = phone.slice(-2);
    return `${'*'.repeat(phone.length - 2)}${lastTwo}`;
  };
  const handleResendCode = async () => {
    if (!currentUser?.user?.phoneNumber || !auth?.data.token) return;

    try {
      await sendVerificationCodePhone(
        `+${CANANA_COUNTRY_CODE}${currentUser.user.phoneNumber}`,
        4,
        auth.data.token
      );

      setSecondsLeft(900); // reset countdown (15 mins)
      setResendEnabled(false);
      setErrorMessage('');
      setValidMessage('');
    } catch (error) {
      console.error('Error resending code:', error);
      const isBlockedPhone = error instanceof Error && error.message === 'phone_number_blocked';
      setErrorMessage(
        isBlockedPhone
          ? 'This phone number has been blocked due to suspicious activity. Please contact support@tenantpay.com for assistance.'
          : 'Failed to resend code. Please try again.'
      );
    }
  };

  const handleVerify = async () => {
    const code = inputValues.join('');
    const isValidCode = code.length === 4 && /^\d{4}$/.test(code);
    if (!isValidCode) {
      setErrorMessage('Please enter a valid code');
      return;
    }
    setErrorMessage('');
    setLoadingVerify(true);
    try {
      const phoneNumber = currentUser?.user?.phoneNumber || '';
      const token = auth?.data.token || '';
      const response = await verifyVerificationCodePhone(
        `+${CANANA_COUNTRY_CODE}${phoneNumber}`,
        token,
        code
      );
      if (response.message === 'verification_successful') {
        setValidMessage('Your phone number has been verified');
        setIsPhoneVerified(true);
        trackOnboardingStepCompleted('verify_phone');
        const response = await getTenantDetails(auth?.data.token);
        if (response?.data?.tenantBuildings.length > 0) {
          setTimeout(() => navigate('/dashboard'), 2000);
        } else {
          setTimeout(() => navigate('/tenant-type'), 2000);
        }

        try {
          const res = await createLoginToken(auth?.data.token || '');
          const loginToken = res.data.loginToken;
          const newAuth = {
            message: 'success',
            data: {
              tenantId: auth?.data.tenantId || '',
              token: loginToken,
            },
          };
          saveAuth(newAuth);
        } catch (err) {
          console.error('Error creating login token:', err);
        }
      } else {
        setErrorMessage('Please enter a valid code');
      }
    } catch (error) {
      const isBlockedPhone = error instanceof Error && error.message === 'phone_number_blocked';
      setErrorMessage(
        isBlockedPhone
          ? 'This phone number has been blocked due to suspicious activity. Please contact support@tenantpay.com for assistance.'
          : 'Please enter a valid code'
      );
    } finally {
      setLoadingVerify(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>, index: number) => {
    if (['+', '-'].includes(e.key)) e.preventDefault();
    else if (e.key === 'Enter') handleVerify();
    else if (e.key === 'Backspace' && inputValues[index] === '' && index > 0)
      inputRefs[index - 1].current?.focus();
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('Text');
    const digits = pastedData.replace(/\D/g, '').substring(0, 4).split('');
    const newValues = [...inputValues];
    for (let i = 0; i < 4; i++) newValues[i] = digits[i] || '';
    setInputValues(newValues);
    inputRefs[digits.length > 0 ? digits.length - 1 : 0].current?.focus();
  };

  useEffect(() => {
    if (currentUser?.user?.phoneNumber && auth?.data.token && !codeSentRef.current) {
      sendVerificationCodePhone(
        `+${CANANA_COUNTRY_CODE}${currentUser.user?.phoneNumber}`,
        4,
        auth.data.token
      ).catch((error) => {
        const isBlockedPhone = error instanceof Error && error.message === 'phone_number_blocked';
        if (isBlockedPhone) {
          setErrorMessage(
            'This phone number has been blocked due to suspicious activity. Please contact support@tenantpay.com for assistance.'
          );
        }
      });

      codeSentRef.current = true;
    }
  }, [currentUser, auth]);
  if (loading) return <AuthBrandedLoader message='Loading…' />;

  return (
    <AuthPageShell variant='center'>
      <div className={loginStyles.authFlowMax}>
        <motion.main
          className={`${loginStyles.main} ${loginStyles.mainWide} ${styles.verifyFlow}`}
          initial={false}
        >
          <button
            type='button'
            className={styles.headerOuter}
            onClick={() => {
              logout();
              navigate('/sign-up');
            }}
          >
            <ChevronLeftIcon className='text-white/70' stroke='currentColor' />
            <p className={styles.emailHeader}>Phone verification</p>
          </button>
          <div className={styles.emailInner}>
            <p className={styles.emailText}>
              A verification code has been sent to{' '}
              <span className='font-bold'>
                {currentUser?.user?.phoneNumber ? maskPhone(currentUser.user.phoneNumber) : ''}
              </span>
            </p>
            <p className={styles.emailText}>
              Please check your messages and enter the verification code below to verify your phone
              number.
            </p>
            <div className={styles.codeBox}>
              <div className={styles.codeBoxInner}>
                <div className={styles.inputBoxes}>
                  {inputRefs.map((ref, index) => (
                    <input
                      key={index}
                      type='number'
                      className={styles.numberBox}
                      ref={ref}
                      placeholder='-'
                      value={inputValues[index]}
                      onChange={handleInputChange(index)}
                      maxLength={1}
                      min={0}
                      max={9}
                      onKeyDown={(e) => handleKeyDown(e, index)}
                      onPaste={handlePaste}
                      onWheel={(e) => (e.target as HTMLInputElement).blur()}
                    />
                  ))}
                </div>
                {errorMessage && <div className={styles.errorMessage}>{errorMessage}</div>}
                {validMessage && <div className={styles.validMessage}>{validMessage}</div>}
              </div>
            </div>

            {codeSentRef.current && (
              <div className={styles.sendOuter}>
                {secondsLeft > 0 ? (
                  <p
                    className={`${styles.emailText} ${styles.timerText} ${
                      secondsLeft <= 30 ? styles.warning : ''
                    }`}
                  >
                    The code will expire in{' '}
                    <span className='font-bold'>{formatTime(secondsLeft)}</span>
                  </p>
                ) : (
                  <p className={styles.emailText}>
                    Code expired. Please click below to resend a new one.
                  </p>
                )}
                <button
                  type='button'
                  className={`${styles.resendCode} ${!resendEnabled && styles.disabledResend}`}
                  onClick={handleResendCode}
                  disabled={!resendEnabled}
                >
                  <RotateCw />
                  <p>Resend code</p>
                </button>
              </div>
            )}
          </div>

          <button
            type='button'
            className={clsx(
              loginStyles.submitBtn,
              (isPhoneVerified || inputValues.some((val) => val === '') || loadingVerify) &&
                loginStyles.disableBtn
            )}
            onClick={handleVerify}
            disabled={isPhoneVerified || inputValues.some((val) => val === '') || loadingVerify}
          >
            {!loadingVerify && <span className='indicator-label'>Next</span>}
            {loadingVerify && (
              <span
                className={styles.indicatorProgress}
                style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
              >
                Please wait...
                <span className={styles.spinner}></span>
              </span>
            )}
          </button>
        </motion.main>
      </div>
    </AuthPageShell>
  );
}
